import type { UpdateClient } from '@wf-coe/types';
import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import { useApiClient } from '../lib/use-api-client.js';
import type { ClientDto } from './use-clients.js';

interface UpdateClientInput {
  id: string;
  patch: UpdateClient;
}

/**
 * Mutation hook for `PUT /v1/clients/{id}`. Invalidates both the
 * `['clients']` list query and the `['clients', id]` detail query on
 * success so the fresh row replaces the cached one across the SPA.
 *
 * Casbin gates `(client, write)` on the server — Admin / Operator only.
 */
export function useUpdateClientMutation(): UseMutationResult<ClientDto, Error, UpdateClientInput> {
  const api = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, patch }: UpdateClientInput) => {
      if (!api) {
        throw new Error('useUpdateClientMutation: api client unavailable (no signed-in account)');
      }
      return api.put<ClientDto>(`/v1/clients/${id}`, patch);
    },
    onSuccess: (_data, { id }) => {
      void queryClient.invalidateQueries({ queryKey: ['clients'] });
      void queryClient.invalidateQueries({ queryKey: ['clients', id] });
    },
  });
}
